import React, { useState } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Button, Stack, Divider, Paper, Chip,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import * as XLSX from 'xlsx';
import PopoverFiltroRelatorio from './PopoverFiltroRelatorio';
import generatePdfRelatorioVendas from './EditPdfRelatorioVendas';
import { cores, coresGrafico } from '../styles/styleUtils';

// ── Paleta TriAxis — aliases dos tokens de styleUtils ─────────────────────
const LARANJA   = cores.laranja;
const CINZA     = cores.chumbo;
const CINZA2    = cores.cinzaMedio;
const LARANJA2  = cores.laranjaEscuro;
const CORES_SEG = coresGrafico;

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtNum = (v) => Number(v || 0).toLocaleString('pt-BR');

// ── KPI Card ──────────────────────────────────────────────────────────────
const KpiCard = ({ label, valor, destaque }) => (
  <Box sx={{
    flex: 1, minWidth: 130, textAlign: 'center',
    border: `1px solid ${destaque ? LARANJA : '#DDD'}`,
    borderRadius: 1, p: 1.5,
    backgroundColor: destaque ? '#FFF3E0' : '#FAFAFA',
    borderLeft: `4px solid ${destaque ? LARANJA : CINZA}`,
  }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="subtitle1" fontWeight="bold" color={destaque ? LARANJA : CINZA}>{valor}</Typography>
  </Box>
);

// ── Título de seção ────────────────────────────────────────────────────────
const SecTitle = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 4 }}>
    <Box sx={{ width: 4, height: 20, backgroundColor: LARANJA, borderRadius: 1 }} />
    <Typography variant="subtitle1" fontWeight="bold" color={CINZA}>{children}</Typography>
  </Box>
);

export default function RelatorioVendasDetalhado({ data, type, filtro, setFiltro, onLimpar }) {
  const [openFiltro, setOpenFiltro] = useState(null);

  const onCloseFiltro   = () => setOpenFiltro(null);
  const limparFiltro    = () => { setOpenFiltro(false); onLimpar(); };
  const onConfirmFiltro = (f) => { setOpenFiltro(false); setFiltro(f); };

  if (!data) return null;

  const r  = data.secao2_resumo;
  const s4 = data.secao4_segmentacoes;

  // ── Dados para gráficos ────────────────────────────────────────────────
  const dadosFormaPagamento = (s4.porFormaPagamento || [])
    .map((d) => ({ name: d.grupo, valor: d.totalVendido, qtd: d.numeroVendas }));

  const dadosFormaAtendimento = (s4.porFormaAtendimento || [])
    .map((d) => ({ name: d.grupo, valor: d.totalVendido, qtd: d.numeroVendas }));

  const dadosFabricante = (s4.porFabricante || [])
    .map((d) => ({ name: d.grupo, valor: d.totalVendido, qtd: d.numeroVendas, potencia: d.potencia }));

  const dadosSexo = (s4.porSexo || [])
    .map((d) => ({ name: d.grupo, value: d.numeroVendas, valor: d.totalVendido }));

  const dadosOrcamentos = Object.entries(data.secao3_orcamentos || {}).map(([k, v]) => ({
    name: k.charAt(0).toUpperCase() + k.slice(1),
    propostas: v.propostas,
    vendas: v.vendas,
    taxa: v.taxa,
  }));

  // ── Meta progress ──────────────────────────────────────────────────────
  const pctMeta = Math.min(r.percentualMeta || 0, 100);

  const exportarExcel = () => {
    const wb = XLSX.utils.book_new();
    const sheetData = [];
    const pushEsp = () => sheetData.push([]);
    const pushTit = (t) => sheetData.push([t]);

    pushTit('1. Vendas Realizadas');
    sheetData.push(['Data','Cliente','Valor','Potência Kit (kWp)','Modelo Inversor','Fornecedor','Vendedor Abertura','Vendedor Fechamento','Estado','Forma Atendimento','Forma Pagamento']);
    data.secao1_vendasRealizadas.forEach((v) => sheetData.push([v.data,v.cliente,v.valor,v.potenciaKit,v.modeloInversor,v.fornecedor,v.vendedorAbertura,v.vendedorFechamento,v.estado,v.formaAtendimento,v.formaPagamento]));

    pushEsp(); pushTit('2. Resumo');
    sheetData.push(['Total Vendido', r.totalVendido]);
    sheetData.push(['Potência Vendida', r.potenciaVendida]);
    sheetData.push(['Quantidade de Vendas', r.qtdVendas]);
    sheetData.push(['Ticket Médio (R$)', r.ticketMedioReais]);
    sheetData.push(['Ticket Médio (kWp)', r.ticketMedioKwp]);
    sheetData.push(['Valor por kWp', r.valorPorKwp]);
    sheetData.push(['Taxa de Conversão (%)', r.taxaConversao]);
    sheetData.push(['Meta do Mês', r.meta]);
    sheetData.push(['% da Meta Atingida', r.percentualMeta]);

    pushEsp(); pushTit('3. Orçamentos por Origem');
    sheetData.push(['Origem','Propostas','Vendas','Taxa Conversão (%)']);
    Object.entries(data.secao3_orcamentos).forEach(([k, v]) => sheetData.push([k.toUpperCase(), v.propostas, v.vendas, v.taxa]));

    const montarSeg = (nome, lista) => {
      pushEsp(); pushTit(`Segmentação por ${nome}`);
      sheetData.push(['Grupo','Total Vendido','Potência','Nº Vendas','Ticket Médio (R$)','Ticket Médio (kWp)','Percentual']);
      lista.forEach((l) => sheetData.push([l.grupo,l.totalVendido,l.potencia,l.numeroVendas,l.ticketMedioReais,l.ticketMedioKwp,l.percentual]));
    };
    montarSeg('Forma de Pagamento', s4.porFormaPagamento);
    montarSeg('Sexo', s4.porSexo);
    montarSeg('Fabricante', s4.porFabricante);
    montarSeg('Forma de Atendimento', s4.porFormaAtendimento);

    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(wb, ws, 'Vendas');
    XLSX.writeFile(wb, 'relatorio-vendas.xlsx');
  };

  return (
    <Box sx={{ mt: 4 }}>
      {/* Barra de ações */}
      <Stack direction="row" justifyContent="flex-end" gap={2} mb={2}>
        <Button variant="outlined" onClick={() => setOpenFiltro(true)}>Filtro</Button>
        <Button variant="outlined" color="secondary" onClick={() => generatePdfRelatorioVendas(data, filtro)}>Exportar em PDF</Button>
        <Button variant="contained" sx={{ backgroundColor: LARANJA, '&:hover': { backgroundColor: LARANJA2 } }} onClick={exportarExcel}>
          Download Excel
        </Button>
      </Stack>

      {/* Filtros ativos */}
      {(filtro?.vendedorAbertura || filtro?.unidade) && (
        <Stack direction="row" gap={1} mb={2} flexWrap="wrap">
          {filtro?.vendedorAbertura && <Chip label={`Vendedor: ${filtro.vendedorAbertura.nome}`} size="small" sx={{ backgroundColor: '#FFF3E0', color: LARANJA }} />}
          {filtro?.unidade && <Chip label={`Unidade: ${filtro.unidade.nome}`} size="small" sx={{ backgroundColor: '#FFF3E0', color: LARANJA }} />}
        </Stack>
      )}

      {/* ── KPIs ─────────────────────────────────────────────────────── */}
      <SecTitle>Resumo de Vendas</SecTitle>
      <Stack direction="row" gap={1.5} flexWrap="wrap" mb={2}>
        <KpiCard label="Total Vendido"       valor={fmtBRL(r.totalVendido)}       destaque />
        <KpiCard label="Quantidade de Vendas" valor={fmtNum(r.qtdVendas)}          destaque />
        <KpiCard label="Potência Vendida"    valor={`${Number(r.potenciaVendida||0).toFixed(2)} kWp`} />
        <KpiCard label="Ticket Médio (R$)"   valor={fmtBRL(r.ticketMedioReais)} />
        <KpiCard label="Ticket Médio (kWp)"  valor={`${Number(r.ticketMedioKwp||0).toFixed(2)} kWp`} />
        <KpiCard label="Valor por kWp"       valor={fmtBRL(r.valorPorKwp)} />
        <KpiCard label="Taxa de Conversão"   valor={`${Number(r.taxaConversao||0).toFixed(2)}%`} />
      </Stack>

      {/* Meta do mês */}
      <Paper variant="outlined" sx={{ p: 2, mb: 4, borderColor: LARANJA }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="body2" fontWeight="bold" color={CINZA}>Meta do Mês</Typography>
          <Typography variant="body2" color={LARANJA} fontWeight="bold">{fmtBRL(r.meta)}</Typography>
        </Stack>
        <Box sx={{ width: '100%', backgroundColor: '#F5F5F5', borderRadius: 1, height: 12, overflow: 'hidden' }}>
          <Box sx={{ width: `${pctMeta}%`, backgroundColor: pctMeta >= 100 ? '#2e7d32' : LARANJA, height: '100%', borderRadius: 1, transition: 'width 0.5s' }} />
        </Box>
        <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
          {Number(r.percentualMeta||0).toFixed(1)}% da meta atingida — {fmtBRL(r.totalVendido)} de {fmtBRL(r.meta)}
        </Typography>
      </Paper>

      {/* ── Gráfico 1 — Vendas por Forma de Pagamento ─────────────────── */}
      <SecTitle>Vendas por Forma de Pagamento</SecTitle>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={dadosFormaPagamento} margin={{ left: 10, right: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
          <Tooltip formatter={(v) => fmtBRL(v)} />
          <Bar dataKey="valor" name="Total Vendido" radius={[4,4,0,0]}>
            {dadosFormaPagamento.map((_, i) => <Cell key={i} fill={CORES_SEG[i % CORES_SEG.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* ── Gráfico 2 — Origem do Atendimento (barras + pizza lado a lado) */}
      <SecTitle>Origem do Atendimento</SecTitle>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
        <Box flex={1.4}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dadosOrcamentos} margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="propostas" name="Propostas" fill={CINZA2}  radius={[4,4,0,0]} />
              <Bar dataKey="vendas"    name="Vendas"    fill={LARANJA} radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
        <Box flex={1}>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={dadosFormaAtendimento} dataKey="qtd" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name}: ${(percent*100).toFixed(0)}%`} labelLine={false}>
                {dadosFormaAtendimento.map((_, i) => <Cell key={i} fill={CORES_SEG[i % CORES_SEG.length]} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [v, n]} />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </Stack>

      {/* ── Gráfico 3 — Vendas por Fabricante ─────────────────────────── */}
      <SecTitle>Vendas por Fabricante</SecTitle>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={dadosFabricante} layout="vertical" margin={{ left: 80, right: 40 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
          <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
          <Tooltip formatter={(v) => fmtBRL(v)} />
          <Bar dataKey="valor" name="Total Vendido" radius={[0,4,4,0]}>
            {dadosFabricante.map((_, i) => <Cell key={i} fill={CORES_SEG[i % CORES_SEG.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* ── Gráfico 4 — Distribuição por Sexo ─────────────────────────── */}
      {dadosSexo.length > 0 && (
        <>
          <SecTitle>Distribuição por Sexo do Cliente</SecTitle>
          <Stack direction="row" justifyContent="center">
            <ResponsiveContainer width="60%" height={200}>
              <PieChart>
                <Pie data={dadosSexo} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                  label={({ name, percent }) => `${name}: ${(percent*100).toFixed(0)}%`} labelLine={false}>
                  {dadosSexo.map((_, i) => <Cell key={i} fill={CORES_SEG[i % CORES_SEG.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => [`${v} vendas`]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </Stack>
        </>
      )}

      <Divider sx={{ my: 3 }} />

      {/* ── Tabela de Vendas ───────────────────────────────────────────── */}
      <SecTitle>Detalhamento das Vendas</SecTitle>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#FFF3E0' }}>
              {['Data','Cliente','Valor','Potência (kWp)','Inversor','Fornecedor','Vend. Abertura','Vend. Fechamento','Estado','Atendimento','Pagamento'].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: 11, color: CINZA }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.secao1_vendasRealizadas.map((v, i) => (
              <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#FFF3E0' } }}>
                <TableCell>{v.data}</TableCell>
                <TableCell>{v.cliente}</TableCell>
                <TableCell align="right">{fmtBRL(v.valor)}</TableCell>
                <TableCell align="center">{Number(v.potenciaKit||0).toFixed(2)}</TableCell>
                <TableCell>{v.modeloInversor}</TableCell>
                <TableCell>{v.fornecedor}</TableCell>
                <TableCell>{v.vendedorAbertura}</TableCell>
                <TableCell>{v.vendedorFechamento}</TableCell>
                <TableCell>{v.estado}</TableCell>
                <TableCell>{v.formaAtendimento}</TableCell>
                <TableCell>{v.formaPagamento}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* ── Resumo de Orçamentos ───────────────────────────────────────── */}
      <SecTitle>Resumo de Orçamentos por Origem</SecTitle>
      <Table size="small" sx={{ maxWidth: 600 }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#FFF3E0' }}>
            <TableCell sx={{ fontWeight: 'bold' }}>Origem</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }} align="center">Propostas</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }} align="center">Vendas</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }} align="center">Conversão (%)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(data.secao3_orcamentos).map(([origem, valores], idx) => (
            <TableRow key={idx} sx={{ '&:hover': { backgroundColor: '#FFF3E0' } }}>
              <TableCell>{origem.charAt(0).toUpperCase() + origem.slice(1)}</TableCell>
              <TableCell align="center">{valores.propostas}</TableCell>
              <TableCell align="center">{valores.vendas}</TableCell>
              <TableCell align="center">{valores.taxa}%</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Divider sx={{ my: 3 }} />

      {/* ── Segmentações ──────────────────────────────────────────────── */}
      <SecTitle>Segmentações</SecTitle>
      {Object.entries(s4).map(([titulo, lista], index) => (
        <Box mt={3} key={index}>
          <Typography variant="body2" fontWeight="bold" color={LARANJA} mb={1}>
            {titulo.replace('por', '').trim().replace(/([A-Z])/g, ' $1').trim()}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F5F5F5' }}>
                {['Grupo','Total Vendido','Potência (kWp)','Nº Vendas','Ticket Médio (R$)','Ticket Médio (kWp)','Percentual (%)'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: 11 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {lista.map((item, i) => (
                <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#FFF3E0' } }}>
                  <TableCell sx={{ width: 200 }}>{item.grupo}</TableCell>
                  <TableCell>{fmtBRL(item.totalVendido)}</TableCell>
                  <TableCell>{Number(item.potencia||0).toFixed(2)}</TableCell>
                  <TableCell align="center">{item.numeroVendas}</TableCell>
                  <TableCell>{fmtBRL(item.ticketMedioReais)}</TableCell>
                  <TableCell>{Number(item.ticketMedioKwp||0).toFixed(2)}</TableCell>
                  <TableCell align="center">{Number(item.percentual||0).toFixed(2)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ))}

      {openFiltro && (
        <PopoverFiltroRelatorio filtro={filtro} open={openFiltro}
          onClose={onCloseFiltro} onLimpar={limparFiltro}
          onConfirm={onConfirmFiltro} type={type} />
      )}
    </Box>
  );
}